import React, { useState, useRef, useEffect, memo } from 'react';
import { ReactComponent as EditIcon } from '../../assets/upload.svg';
import axiosInstance from '../../utils/axiosConfig';
import { useNavigate } from 'react-router-dom';

const ProfileSetupPage = memo(({ onComplete, onPrev }) => {
    const [name, setName] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState("");
    const [isNameValid, setIsNameValid] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const MAX_NAME_LENGTH = 30;
    const navigate = useNavigate();

    // Валидация имени при его изменении
    useEffect(() => {
        setIsNameValid(name.trim().length > 0);
    }, [name]);

    // Обработка изменения аватара
    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            // Создание предварительного просмотра
            const reader = new FileReader();
            reader.onloadend = () => {
                setPreviewUrl(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Открытие диалога выбора файла при нажатии на кнопку редактирования
    const handleEditClick = () => {
        fileInputRef.current.click();
    };

    // Завершение настройки профиля
    const handleCompleteSetup = async () => {
        if (!isNameValid || isSubmitting) return;

        setIsSubmitting(true);
        setError(null);

        try {
            const isTelegram = window.Telegram?.WebApp?.initData;
            const authToken = localStorage.getItem('authToken');

            if (!isTelegram && !authToken) {
                throw new Error('Не удалось получить данные авторизации. Пожалуйста, обновите страницу.');
            }

            const nameResponse = await axiosInstance.post('/profile/save-name', {
                name,
            });

            if (!nameResponse.data.success) {
                throw new Error(nameResponse.data.error || 'Ошибка при сохранении имени');
            }

            let avatarUrl = getTelegramPhotoUrl();

            if (selectedFile) {
                const formData = new FormData();
                formData.append('avatar', selectedFile);

                const avatarResponse = await axiosInstance.post('/profile/save-avatar', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    }
                });

                if (!avatarResponse.data.success) {
                    throw new Error(avatarResponse.data.error || 'Ошибка при загрузке аватара');
                }

                avatarUrl = avatarResponse.data.avatarUrl;
            }

            const completeResponse = await axiosInstance.post('/profile/complete-setup', {});

            if (!completeResponse.data.success) {
                throw new Error(completeResponse.data.error || 'Ошибка при завершении настройки');
            }

            if (onComplete) {
                onComplete({
                    name,
                    avatar: avatarUrl
                });
            }

            navigate('/');

            setTimeout(() => {
                window.location.href = '/';
            }, 300);

        } catch (err) {
            setError(err.response?.status === 413 ? 'Изображение превышает допустимый размер (25 МБ)' : err.message || 'Произошла ошибка при настройке профиля');
            console.error('Ошибка при настройке профиля:', err);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Функция для получения фото пользователя из Telegram
    const getTelegramPhotoUrl = () => {
        if (!window.Telegram?.WebApp?.initDataUnsafe?.user) return null;
        return window.Telegram.WebApp.initDataUnsafe.user.photo_url || null;
    };

    // Отображаемый аватар
    const avatarSrc = previewUrl || getTelegramPhotoUrl() || '/default-avatar.png';

    return (
        <div className="profile-setup-container">
            <h2 className="profile-setup-title">Добавьте фото и имя</h2>
            <p className="profile-setup-subtitle">(*обязательные поля)</p>

            <div className="profile-setup-content">
                <div className="avatar-section">
                    <div className="avatar-container">
                        <img
                            src={avatarSrc}
                            alt="Аватар"
                            className="avatar-preview"
                        />

                        <svg className="avatar-corner top-left" width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 21.1689H4.00561V3.82842H22.1487V0H0V21.1689Z" fill="#00739F" />
                        </svg>

                        <svg className="avatar-corner top-right" width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 21.1689H18.9944V3.82842H0.8513V0H23V21.1689Z" fill="#00739F" />
                        </svg>

                        <svg className="avatar-corner bottom-left" width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 0.8311H4.00561V18.1716H22.1487V22H0V0.8311Z" fill="#00739F" />
                        </svg>

                        <svg className="avatar-corner bottom-right" width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M23 0.8311H18.9944V18.1716H0.8513V22H23V0.8311Z" fill="#00739F" />
                        </svg>
                    </div>
                    <p className="profile-setup-subtitle">Изображение до 25 МБ</p>
                    <button
                        className="upload-photo-button"
                        onClick={handleEditClick}
                        disabled={isSubmitting}
                    >
                        <EditIcon className="edit-icon" />
                        Загрузить свою фотографию
                    </button>

                    {/* Скрытый input для выбора файла */}
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleFileChange}
                        className="file-input"
                        disabled={isSubmitting}
                    />
                </div>

                <div className="name-section">
                    <div className="name-field-wrapper">
                        <div className="name-field-background">
                            <div className="background-rect"></div>
                            <svg className="corner top-left" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M34 2H13.6364L2 12.2655V31" stroke="#009F03" strokeWidth="5" strokeMiterlimit="10" />
                            </svg>
                            <svg className="corner top-right" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 2H36.3636L48 12.2655V31" stroke="#009F03" strokeWidth="5" strokeMiterlimit="10" />
                            </svg>
                            <svg className="corner bottom-left" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M34 48H13.6364L2 37.7345V19" stroke="#009F03" strokeWidth="5" strokeMiterlimit="10" />
                            </svg>
                            <svg className="corner bottom-right" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16 48H36.3636L48 37.7345V19" stroke="#009F03" strokeWidth="5" strokeMiterlimit="10" />
                            </svg>
                        </div>

                        <div className="name-input-container">
                            <label htmlFor="name-input" className="name-input-label">*Введите Ваше имя</label>
                            <input
                                id="name-input"
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="name-input"
                                maxLength={MAX_NAME_LENGTH}
                                disabled={isSubmitting}
                            />
                            <div className="character-counter">
                                {name.length}/{MAX_NAME_LENGTH}
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="error-message">{error}</div>
                )}
            </div>

            <div className="profile-setup-navigation">
                <button
                    className="setup-button prev-button"
                    onClick={onPrev}
                    disabled={isSubmitting}
                >
                    Назад
                </button>

                <button
                    className="setup-button complete-button"
                    onClick={handleCompleteSetup}
                    disabled={!isNameValid || isSubmitting}
                >
                    {isSubmitting ? 'Загрузка...' : 'Подтвердить'}
                </button>
            </div>
        </div>
    );
});

export default ProfileSetupPage; 